
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Download } from "lucide-react";
import { generateJobContractPDF } from "@/utils/jobContractPDFGenerator";

interface JobContract {
  id: string;
  contract_type: string;
  template_name: string;
  contract_data: any;
  generated_at: string;
  start_date: string;
  end_date: string | null;
  hourly_rate: number | null;
  monthly_salary: number | null;
  working_hours_per_week: number;
  status: string;
  employee_name?: string;
  employee_email?: string;
}

export function JobContractList() {
  const [contracts, setContracts] = useState<JobContract[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      // Check admin status first
      const { data: isAdmin } = await supabase.rpc('check_is_admin');
      if (!isAdmin) {
        toast({
          title: "Zugriff verweigert",
          description: "Sie haben keine Berechtigung für diese Aktion",
          variant: "destructive",
        });
        return;
      }

      // For now, we'll show a placeholder until the database types are updated
      // This simulates contracts that would be stored in the job_contracts table
      const mockContracts: JobContract[] = [
        {
          id: '1',
          contract_type: 'hourly',
          template_name: 'Standard Swiss Employment Contract',
          contract_data: {
            job_title: 'Software Developer',
            department: 'IT',
            job_description: 'Development of web applications'
          },
          generated_at: new Date().toISOString(),
          start_date: '2025-01-01',
          end_date: null,
          hourly_rate: 50.00,
          monthly_salary: null,
          working_hours_per_week: 42,
          status: 'active',
          employee_name: 'Max Mustermann',
          employee_email: 'max.mustermann@example.com'
        }
      ];

      setContracts(mockContracts);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (contract: JobContract) => {
    try {
      // Create a comprehensive employee object for PDF generation
      const comprehensiveEmployee = {
        id: '1',
        name: contract.employee_name || 'Unbekannt',
        firstname: contract.employee_name?.split(' ')[0] || 'Unbekannt',
        lastname: contract.employee_name?.split(' ')[1] || 'Unbekannt',
        email: contract.employee_email || '',
        birth_date: '1990-01-01',
        address: 'Musterstrasse 1',
        city: 'Zürich',
        postal_code: '8001',
        country: 'Switzerland',
        ahv_number: '756.1234.5678.90',
        iban: 'CH93 0076 2011 6238 5295 7',
        bank_name: 'UBS',
        health_insurance_provider: 'CSS Versicherung',
        accident_insurance_number: 'UV-123456',
        pension_fund_number: 'PK-789012',
        bank_clearing_number: '254',
        tax_municipality: 'Zürich',
        tax_canton: 'ZH',
        marital_status: 'single',
        gender: 'prefer_not_to_say',
        nationality: 'Swiss',
        has_children: false,
        kinderzulagen_eligible: false,
        work_permit_type: undefined,
        work_permit_expires: undefined
      };

      await generateJobContractPDF({
        contract_id: contract.id,
        employee: comprehensiveEmployee,
        contract_type: contract.contract_type as "hourly" | "monthly",
        start_date: contract.start_date,
        end_date: contract.end_date || undefined,
        hourly_rate: contract.hourly_rate || undefined,
        monthly_salary: contract.monthly_salary || undefined,
        working_hours_per_week: contract.working_hours_per_week,
        probation_period_months: contract.contract_data.probation_period_months || 3,
        vacation_days: contract.contract_data.vacation_days || 25,
        notice_period_days: contract.contract_data.notice_period_days || 30,
        job_title: contract.contract_data.job_title || '',
        department: contract.contract_data.department || '',
        job_description: contract.contract_data.job_description || '',
        employee_id: '1',
      });

      toast({
        title: "Erfolg",
        description: "PDF wurde erfolgreich generiert",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Generieren der PDF",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'terminated':
        return 'text-red-600 bg-red-100';
      case 'draft':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktiv';
      case 'terminated':
        return 'Beendet';
      case 'draft':
        return 'Entwurf';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Arbeitsverträge</CardTitle>
      </CardHeader>
      <CardContent>
        {contracts.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Keine Verträge gefunden</p>
        ) : (
          <div className="space-y-4">
            {contracts.map((contract) => (
              <div key={contract.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <div>
                        <h3 className="font-semibold">{contract.employee_name}</h3>
                        <p className="text-sm text-gray-500">{contract.employee_email}</p>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                      <div>
                        <span className="font-medium">Typ:</span> {contract.contract_type === 'hourly' ? 'Stundenlohn' : 'Monatslohn'}
                      </div>
                      <div>
                        <span className="font-medium">Start:</span> {new Date(contract.start_date).toLocaleDateString('de-CH')}
                      </div>
                      <div>
                        <span className="font-medium">Lohn:</span> {
                          contract.contract_type === 'hourly' 
                            ? `CHF ${contract.hourly_rate}/h`
                            : `CHF ${contract.monthly_salary}/Mt`
                        }
                      </div>
                      <div>
                        <span className="font-medium">Std/Woche:</span> {contract.working_hours_per_week}h
                      </div>
                      <div>
                        <span className="font-medium">Position:</span> {contract.contract_data.job_title || 'Nicht angegeben'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                      {getStatusLabel(contract.status)}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(contract)}
                      title="PDF herunterladen"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Erstellt: {new Date(contract.generated_at).toLocaleDateString('de-CH')} um {new Date(contract.generated_at).toLocaleTimeString('de-CH')}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
